import { useState } from 'react';
import { useDynamicEntityStoreContext, useDynamicEntityCMSDataContext } from '../contexts';

import SwitchInput from '../../inputs/form-inputs/SwitchInput';
import TextAreaInput from '../../inputs/form-inputs/TextAreaInput';
import DropdownInput from '../../inputs/form-inputs/DropdownInput';

import { ERRORS } from '../lib';
import { formatDateTime } from '../../utils/DateFormatters';
import { getCurrentSlugParam, getParentSlugParam, toUrlSlug } from '../../utils/UrlFormatters';

function UrlSlugInput({ setIsTouched }) {
  const assignedCategoryUrlPattern = useDynamicEntityStoreContext((state) => state.data.assigned_category?.url_pattern);
  const urlSlug = useDynamicEntityStoreContext((state) => state.data.url_slug);
  const parentSlug = useDynamicEntityStoreContext((state) => state.parentSlug);
  const errorKeys = useDynamicEntityStoreContext((state) => state.errorKeys);
  const urlSlugHasError = errorKeys.includes(ERRORS[1].key) || errorKeys.includes(ERRORS[2].key);

  const updateData = useDynamicEntityStoreContext((state) => state.updateData);

  if (assignedCategoryUrlPattern) {
    return (
      <>
        <TextAreaInput
          id="page-settings-assigned-category-url-input"
          className="form-control text-area-fit"
          value={assignedCategoryUrlPattern || ''}
          disabled
          rows={1}
        />
        <span className="text-info">URL Slug here now follows the assigned category's URL</span>
      </>
    );
  }

  if (parentSlug) {
    return (
      <div className="input-group mb-3">
        <span className="input-group-text" id="parent-slug" style={{ maxWidth: 170, overflow: 'scroll' }}>
          {parentSlug === '/' ? '/' : `${parentSlug}/`}
        </span>
        <div className={`form-control py-0 ps-0 ${urlSlugHasError ? 'is-invalid-field' : ''}`}>
          <TextAreaInput
            id="page-settings-url-slug-input"
            className="form-control text-area-fit"
            value={urlSlug || ''}
            onChange={(e) => {
              updateData({ url_slug: e.target.value });
              setIsTouched(true);
            }}
            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, height: '100%' }}
            rows={1}
          />
        </div>
      </div>
    );
  }
  return (
    <div className={urlSlugHasError ? 'is-invalid-field' : undefined}>
      <TextAreaInput
        id="page-settings-url-slug-input"
        className="form-control text-area-fit"
        value={urlSlug || ''}
        onChange={(e) => {
          updateData({ url_slug: e.target.value });
          setIsTouched(true);
        }}
        rows={1}
      />
    </div>
  );
}

export default function PageSettings() {
  const { categoriesWithTemplates, endpoints, isTemplate, entityTitle } = useDynamicEntityCMSDataContext();
  const { previewUrl } = endpoints;

  const pageId = useDynamicEntityStoreContext((state) => state.data.id);
  const isAssignedToJobs = useDynamicEntityStoreContext((state) => state.data.is_assigned_to_jobs);

  const title = useDynamicEntityStoreContext((state) => state.data.title);
  const urlSlug = useDynamicEntityStoreContext((state) => state.data.url_slug);
  const updatedAt = useDynamicEntityStoreContext((state) => state.data.updated_at);
  const createdAt = useDynamicEntityStoreContext((state) => state.data.created_at);
  const isDraft = useDynamicEntityStoreContext((state) => state.data.is_draft);
  const categoryId = useDynamicEntityStoreContext((state) => state.data.category_id);
  const assignedCategory = useDynamicEntityStoreContext((state) => state.data.assigned_category);
  const hasAssignedCategoryChanged = useDynamicEntityStoreContext((state) => state.hasAssignedCategoryChanged);

  const update = useDynamicEntityStoreContext((state) => state.update);
  const updateData = useDynamicEntityStoreContext((state) => state.updateData);

  const errorKeys = useDynamicEntityStoreContext((state) => state.errorKeys);

  const urlSlugHasError = errorKeys.includes(ERRORS[1].key) || errorKeys.includes(ERRORS[2].key);
  const urlSlugErrorMsg = urlSlugHasError ? (errorKeys.includes(ERRORS[1].key) ? ERRORS[1].message : ERRORS[2].message) : null;

  const [isTouched, setIsTouched] = useState(false);

  const shouldAutoGenerateSlug = () => {
    // Auto-generate URL slug if:
    // 1) not assigned to jobs
    // 2) not editing an existing page (no id, no current_slug, no parent_slug)
    // 3) the user has not interacted with the url slug input at all
    return !isTouched && !isAssignedToJobs && !pageId && !getCurrentSlugParam() && !getParentSlugParam();
  };

  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex justify-content-between">
        <div className="d-flex flex-column">
          <h3 className="mb-1">{entityTitle} Settings</h3>
          <p className="text-muted mb-0">Configure the {entityTitle}'s settings</p>
        </div>
      </div>
      <div className="d-flex flex-column">
        {updatedAt && (
          <p className="text-muted mb-0">
            <strong>Updated at: </strong>
            <span className="fst-italic">{formatDateTime(updatedAt)}</span>
          </p>
        )}
        {createdAt && (
          <p className="text-muted mb-0">
            <strong>Created at: </strong>
            <span className="fst-italic">{formatDateTime(createdAt)}</span>
          </p>
        )}
      </div>

      {title && urlSlug && (
        <button className="settings-success" type="button" onClick={() => update({ activeTab: 1 })}>
          Great, you can now move to the next step. <i className="fe fe-arrow-right" />
        </button>
      )}
      <div className="d-flex flex-column gap-3 mx-2">
        <div className="d-flex flex-column gap-2">
          <label>
            {entityTitle} Title {errorKeys.includes(ERRORS[0].key) && <small className="text-danger">({ERRORS[0].message})</small>}
          </label>
          <div className={errorKeys.includes(ERRORS[0].key) ? 'is-invalid-field' : undefined}>
            <TextAreaInput
              id="page-settings-title-input"
              className="form-control text-area-fit"
              value={title || ''}
              onChange={(e) =>
                updateData({
                  title: e.target.value,
                  ...(shouldAutoGenerateSlug() ? { url_slug: toUrlSlug(e.target.value) } : {}),
                })
              }
              rows={1}
            />
          </div>
        </div>
        <div className="d-flex flex-column gap-2">
          <label>URL Slug {urlSlugHasError && <small className="text-danger">({urlSlugErrorMsg})</small>}</label>
          <UrlSlugInput setIsTouched={setIsTouched} />
        </div>
        <div className="d-flex flex-column gap-2">
          <label>Draft</label>
          <SwitchInput id="page-settings-is-draft-input" checked={!!isDraft} onChange={(e) => updateData({ is_draft: e.target.checked })} />
        </div>
        {isTemplate && (
          <>
            <hr className="my-3" />
            <div className="d-flex flex-column">
              <h3>Assign to</h3>
              <p className="text-muted">Select ONE of the following to assign this template to:</p>
              <ol>
                <li className="mb-3">
                  <div className="d-flex flex-column gap-2">
                    <label>Assign to category</label>
                    <DropdownInput
                      formattedDropdownValues={categoriesWithTemplates.map((category) => ({
                        value: category.id,
                        label: category.name,
                      }))}
                      value={categoryId}
                      onChange={(e) => updateData({ category_id: e.value, is_assigned_to_jobs: false })}
                      placeholder="Select a category"
                      isDisabled={isAssignedToJobs}
                      isClearable
                    />
                    {assignedCategory && (
                      <button className="btn btn-link" type="button" onClick={() => (window.location.href = previewUrl)} disabled={hasAssignedCategoryChanged}>
                        Visit preview <i className="fe fe-external-link" />
                      </button>
                    )}
                    {hasAssignedCategoryChanged && (
                      <p className="text-warning mb-0">
                        You have changed the category assignment. Please save before proceeding to edit other fields or to preview.
                      </p>
                    )}
                  </div>
                </li>
                <li>
                  <div className="d-flex flex-column gap-2">
                    <label>Assign to jobs</label>
                    <SwitchInput
                      id="page-settings-assign-to-jobs-input"
                      checked={!!isAssignedToJobs}
                      onChange={(e) => updateData({ is_assigned_to_jobs: e.target.checked, category_id: null })}
                      disabled={assignedCategory || categoryId}
                    />
                  </div>
                </li>
              </ol>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
